/**
 * Lern-Quiz — spielerische Wissens-Checks pro Bodenblick-Thema.
 * Route: /lernquiz (PUBLIC). Gedacht für Schulklassen & Neugierige.
 */
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Leaf, Flower, Fire, Clock, CloudSun,
  CheckCircle, XCircle, ArrowRight, ArrowCounterClockwise, Trophy,
} from "@phosphor-icons/react";
import { QUIZ_TOPICS } from "@/data/quizData";

const ICONS = { Leaf, Flower, Fire, Clock, CloudSun };

function TopicCard({ topic, onStart }) {
  const Icon = ICONS[topic.icon] || Leaf;
  return (
    <motion.button
      whileHover={{ y: -4 }}
      onClick={() => onStart(topic)}
      data-testid={`quiz-topic-${topic.id}`}
      className="text-left bg-[#121212] border border-white/10 p-6 rounded-2xl transition-colors hover:border-white/30 group"
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${topic.color}1A`, color: topic.color }}
      >
        <Icon size={24} weight="fill" />
      </div>
      <div className="font-display text-xl font-bold tracking-tight">{topic.label}</div>
      <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{topic.teaser}</p>
      <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest" style={{ color: topic.color }}>
        {topic.questions.length} Fragen <ArrowRight size={12} weight="bold" />
      </div>
    </motion.button>
  );
}

function ScoreScreen({ topic, score, total, onRestart, onExit }) {
  const pct = Math.round((score / total) * 100);
  const msg =
    pct === 100 ? "Perfekt! Du bist bereit fürs Kommandozentrum." :
    pct >= 66 ? "Stark! Fast alles gewusst." :
    pct >= 33 ? "Solide Basis — noch eine Runde?" :
    "Guter Start — beim zweiten Versuch klappt mehr.";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-[#121212] border border-white/10 rounded-2xl p-8 sm:p-12 text-center"
      data-testid="quiz-score-screen"
    >
      <Trophy size={44} weight="fill" style={{ color: topic.color }} className="mx-auto" />
      <div className="font-display text-5xl sm:text-6xl font-bold tracking-tighter mt-4" data-testid="quiz-score-value">
        {score} / {total}
      </div>
      <div className="text-sm font-mono uppercase tracking-widest text-zinc-500 mt-2">{pct} % richtig</div>
      <p className="text-base text-white/80 mt-4">{msg}</p>
      <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
        <button
          onClick={onRestart}
          data-testid="quiz-restart-btn"
          className="inline-flex items-center gap-2 bg-[#00FA9A] text-black px-5 py-3 text-sm font-bold uppercase tracking-widest rounded-full hover:bg-[#33FBAE] transition-colors"
        >
          <ArrowCounterClockwise size={14} weight="bold" /> Nochmal
        </button>
        <button
          onClick={onExit}
          data-testid="quiz-exit-btn"
          className="inline-flex items-center gap-2 border border-white/15 text-white px-5 py-3 text-sm font-bold uppercase tracking-widest rounded-full hover:bg-white/5 transition-colors"
        >
          Anderes Thema
        </button>
      </div>
    </motion.div>
  );
}

function QuizRunner({ topic, onExit }) {
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const q = topic.questions[idx];
  const total = topic.questions.length;

  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === q.correct) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= total) { setDone(true); return; }
    setIdx((v) => v + 1);
    setPicked(null);
  };

  const restart = () => { setIdx(0); setPicked(null); setScore(0); setDone(false); };

  if (done) {
    return <ScoreScreen topic={topic} score={score} total={total} onRestart={restart} onExit={onExit} />;
  }

  return (
    <div data-testid="quiz-runner">
      <div className="flex items-center justify-between gap-3 mb-4">
        <button onClick={onExit} data-testid="quiz-back-btn" className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors">
          ← Themen
        </button>
        <span className="text-xs font-mono uppercase tracking-widest" style={{ color: topic.color }} data-testid="quiz-progress-label">
          Frage {idx + 1} / {total} · {score} richtig
        </span>
      </div>
      <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-6">
        <motion.div
          className="h-full"
          style={{ background: topic.color }}
          animate={{ width: `${((idx + (picked !== null ? 1 : 0)) / total) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.25 }}
          className="bg-[#121212] border border-white/10 rounded-2xl p-6 sm:p-8"
        >
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-snug" data-testid="quiz-question-text">
            {q.q}
          </h2>
          <div className="mt-6 space-y-3">
            {q.options.map((opt, i) => {
              const isCorrect = i === q.correct;
              const isPicked = picked === i;
              const show = picked !== null;
              let cls = "border-white/15 hover:border-white/40 hover:bg-white/5";
              if (show && isCorrect) cls = "border-[#00FA9A]/70 bg-[#00FA9A]/10";
              else if (show && isPicked) cls = "border-[#FF5500]/70 bg-[#FF5500]/10";
              else if (show) cls = "border-white/10 opacity-50";
              return (
                <button
                  key={i}
                  onClick={() => pick(i)}
                  disabled={picked !== null}
                  data-testid={`quiz-option-${i}`}
                  className={`w-full text-left border rounded-xl px-4 py-3.5 text-sm sm:text-base transition-colors flex items-center gap-3 ${cls}`}
                >
                  <span className="font-mono text-xs text-zinc-500 shrink-0">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1">{opt}</span>
                  {show && isCorrect && <CheckCircle size={20} weight="fill" className="text-[#00FA9A] shrink-0" />}
                  {show && isPicked && !isCorrect && <XCircle size={20} weight="fill" className="text-[#FF6A5A] shrink-0" />}
                </button>
              );
            })}
          </div>

          {picked !== null && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 border-l-2 pl-4 py-1"
              style={{ borderColor: topic.color }}
              data-testid="quiz-explanation"
            >
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: topic.color }}>
                {picked === q.correct ? "Richtig!" : "Leider nein — Klartext:"}
              </div>
              <p className="text-sm text-white/80 leading-relaxed">{q.explain}</p>
              <button
                onClick={next}
                data-testid="quiz-next-btn"
                className="mt-4 inline-flex items-center gap-2 bg-[#00FA9A] text-black px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-full hover:bg-[#33FBAE] transition-colors"
              >
                {idx + 1 >= total ? "Ergebnis ansehen" : "Nächste Frage"} <ArrowRight size={12} weight="bold" />
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function LernQuiz() {
  const [topic, setTopic] = useState(null);

  return (
    <div className="space-y-8 max-w-4xl" data-testid="lernquiz-page">
      <Helmet>
        <title>Lern-Quiz · bodenblick.com</title>
        <meta name="description" content="Teste dein Wissen über Boden, Bienen, Waldbrand, Klima und Wetter — 5 Themen, 30 Fragen, Klartext-Erklärungen." />
      </Helmet>

      <header>
        <div className="eid-label flex items-center gap-1.5">
          <GraduationCap size={12} weight="fill" /> Lern-Quiz · für Schulklassen & Neugierige
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter bb-accent-stripe">
          Wie gut kennst du deinen Boden?
        </h1>
        <p className="mt-5 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
          5 Themen, je 6 Fragen. Zu jeder Antwort gibt es eine Klartext-Erklärung —
          falsch liegen ist hier ausdrücklich erlaubt.
        </p>
      </header>

      {!topic ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="quiz-topic-grid">
          {QUIZ_TOPICS.map((t) => (
            <TopicCard key={t.id} topic={t} onStart={setTopic} />
          ))}
        </div>
      ) : (
        <QuizRunner topic={topic} onExit={() => setTopic(null)} />
      )}
    </div>
  );
}
